import { useMemo } from 'react';

import { cn } from '@/lib/utils';
import type { DailyReportClass } from '@/types/dailyReport';
import { formatInstituteDate } from '../../utils';

export interface SubjectCoverageCardProps {
  classes: DailyReportClass[];
  /** The day on screen, `YYYY-MM-DD` — a past report is not "today". */
  date: string;
  isToday: boolean;
}

/**
 * The colours the bars cycle through.
 *
 * Spelled out in full because Tailwind scans the source for literal class
 * names, and taken from the palette rather than this project's `var()` tokens
 * so a run of six subjects is six distinguishable bars rather than six of the
 * same accent. The subject's name is beside every bar, so the colour is never
 * what carries the meaning.
 */
const BAR_TONES = [
  'bg-violet-500',
  'bg-blue-500',
  'bg-emerald-500',
  'bg-amber-500',
  'bg-rose-500',
  'bg-sky-500',
  'bg-teal-500',
  'bg-indigo-500',
] as const;

/**
 * How much of each subject's teaching on the day has been written up.
 *
 * Counted from the day's own classes, so a subject taught twice needs both
 * lessons written before it reads 100%. This is coverage of the *report*, not
 * of the syllabus — the screen says "class coverage" for that reason, and a
 * teacher who has written nothing sees zero rather than an empty panel.
 */
export function SubjectCoverageCard({ classes, date, isToday }: SubjectCoverageCardProps) {
  const subjects = useMemo(() => {
    const bySubject = new Map<string, { name: string; taught: number; written: number }>();

    for (const entry of classes) {
      const current = bySubject.get(entry.subject.uuid) ?? {
        name: entry.subject.name,
        taught: 0,
        written: 0,
      };

      current.taught += 1;
      if (entry.report) current.written += 1;
      bySubject.set(entry.subject.uuid, current);
    }

    return [...bySubject.values()].map((subject) => ({
      ...subject,
      percent: subject.taught === 0 ? 0 : Math.round((subject.written / subject.taught) * 100),
    }));
  }, [classes]);

  return (
    <section className="card flex flex-col rounded-[16px] border border-line bg-surface p-5 shadow-2xs">
      <h3 className="text-base font-bold tracking-normal text-ink">Subject-wise Class Coverage</h3>
      <p className="mt-0.5 text-xs font-medium text-ink-3">
        How much of each subject you have written up {isToday ? 'today' : `for ${formatInstituteDate(date)}`}
      </p>

      {subjects.length === 0 ? (
        <p className="mt-5 text-sm font-medium text-ink-3">
          Nothing is timetabled for you on this day, so there is nothing to cover.
        </p>
      ) : (
        <ul className="mt-4 flex flex-col gap-3">
          {subjects.map((subject, index) => (
            <li key={subject.name} className="flex items-center gap-3">
              <span className="w-20 shrink-0 truncate text-sm font-bold text-ink" title={subject.name}>
                {subject.name}
              </span>

              <span className="h-2 flex-1 overflow-hidden rounded-full bg-surface-3">
                <span
                  className={cn(
                    'block h-full rounded-full transition-all',
                    BAR_TONES[index % BAR_TONES.length],
                  )}
                  style={{ width: `${subject.percent}%` }}
                />
              </span>

              <span
                className="w-12 shrink-0 text-right text-sm font-bold tabular-nums text-ink-2"
                title={`${subject.written} of ${subject.taught} written up`}
              >
                {subject.percent}%
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
